import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { toast } from 'react-toastify';
import { AdminPermissionData } from 'services/dataService';

const ACTIONS = [
  { key: 'view', label: 'Visualizar' },
  { key: 'create', label: 'Inserir' },
  { key: 'edit', label: 'Editar' },
  { key: 'delete', label: 'Excluir' },
];

const useStyles = makeStyles((theme) => ({
  section: {
    backgroundColor: theme.palette.background.default,
    fontWeight: 600,
  },
  actionCell: {
    width: 90,
    textAlign: 'center',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

function fullAccessGrid(catalog) {
  return catalog.reduce((grid, menu) => {
    grid[menu.key] = ACTIONS.reduce((flags, action) => {
      flags[action.key] = menu.actions.includes(action.key);
      return flags;
    }, {});
    return grid;
  }, {});
}

function gridFromPermissions(catalog, permissions) {
  return catalog.reduce((grid, menu) => {
    const current = permissions?.[menu.key] || {};
    grid[menu.key] = ACTIONS.reduce((flags, action) => {
      flags[action.key] =
        menu.actions.includes(action.key) && current[action.key] === true;
      return flags;
    }, {});
    return grid;
  }, {});
}

const AdminPermissionsDialog = ({ open, onClose, userId, userName }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [shared, setShared] = useState([]);
  const [targetIsSuperAdmin, setTargetIsSuperAdmin] = useState(false);
  const [restricted, setRestricted] = useState(false);
  const [grid, setGrid] = useState({});

  useEffect(() => {
    if (!open || !userId) return;

    const load = async () => {
      setLoading(true);
      try {
        const { data: userData } = await AdminPermissionData.getByUser(userId);
        const catalogData = userData.catalog || [];

        setCatalog(catalogData);
        setShared(userData.shared || []);
        setTargetIsSuperAdmin(userData.is_super_admin);
        setRestricted(userData.restricted);
        setGrid(
          userData.restricted
            ? gridFromPermissions(catalogData, userData.permissions)
            : fullAccessGrid(catalogData)
        );
      } catch (error) {
        toast.error('Não foi possível carregar as permissões.');
        onClose();
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, userId]);

  const sections = useMemo(
    () =>
      catalog.reduce((result, menu) => {
        const last = result[result.length - 1];
        if (last && last.name === menu.section) {
          last.menus.push(menu);
        } else {
          result.push({ name: menu.section, menus: [menu] });
        }
        return result;
      }, []),
    [catalog]
  );

  const toggle = (menuKey, actionKey) => {
    setGrid((previous) => {
      const flags = { ...previous[menuKey] };
      const menu = catalog.find((item) => item.key === menuKey);
      const value = !flags[actionKey];
      flags[actionKey] = value;

      if (actionKey === 'view' && !value) {
        ACTIONS.forEach((action) => (flags[action.key] = false));
      } else if (actionKey !== 'view' && value && menu.actions.includes('view')) {
        flags.view = true;
      }

      const next = { ...previous, [menuKey]: flags };

      shared
        .filter((group) => group.menus.includes(menuKey))
        .forEach(({ action, menus }) => {
          const linkedValue = flags[action] === true;
          menus
            .filter((key) => key !== menuKey)
            .forEach((key) => {
              next[key] = {
                ...next[key],
                [action]: linkedValue,
                view: linkedValue ? true : next[key]?.view,
              };
            });
        });

      return next;
    });
  };

  const toggleColumn = (actionKey, value) => {
    setGrid((previous) => {
      const next = { ...previous };
      catalog.forEach((menu) => {
        if (!menu.actions.includes(actionKey)) return;
        const flags = { ...next[menu.key], [actionKey]: value };
        if (actionKey === 'view' && !value) {
          ACTIONS.forEach((action) => (flags[action.key] = false));
        } else if (actionKey !== 'view' && value) {
          flags.view = true;
        }
        next[menu.key] = flags;
      });
      return next;
    });
  };

  const columnState = (actionKey) => {
    const menus = catalog.filter((menu) => menu.actions.includes(actionKey));
    const checked = menus.filter((menu) => grid[menu.key]?.[actionKey]).length;
    return {
      all: menus.length > 0 && checked === menus.length,
      some: checked > 0 && checked < menus.length,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await AdminPermissionData.updateByUser(userId, {
        restricted,
        permissions: catalog.map((menu) => ({
          menu: menu.key,
          can_view: grid[menu.key]?.view === true,
          can_create: grid[menu.key]?.create === true,
          can_edit: grid[menu.key]?.edit === true,
          can_delete: grid[menu.key]?.delete === true,
        })),
      });
      toast.success('Permissões salvas.');
      onClose();
    } catch (error) {
      toast.error('Não foi possível salvar as permissões.');
    } finally {
      setSaving(false);
    }
  };

  const gridDisabled = !restricted || targetIsSuperAdmin;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Permissões do administrador
        {userName && (
          <Typography variant="body2" color="textSecondary">
            {userName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box className={classes.loading}>
            <CircularProgress />
          </Box>
        ) : targetIsSuperAdmin ? (
          <Typography>
            Este usuário é super admin e não pode ter o acesso restringido.
          </Typography>
        ) : (
          <>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={!restricted}
                  onChange={(event) => setRestricted(!event.target.checked)}
                />
              }
              label="Acesso total ao painel administrativo"
            />
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Com o acesso total desligado, o administrador vê e altera apenas o
              que estiver marcado abaixo.
            </Typography>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Menu</TableCell>
                  {ACTIONS.map((action) => {
                    const state = columnState(action.key);
                    return (
                      <TableCell key={action.key} className={classes.actionCell}>
                        {action.label}
                        <br />
                        <Checkbox
                          size="small"
                          color="primary"
                          disabled={gridDisabled}
                          checked={state.all}
                          indeterminate={state.some}
                          onChange={(event) =>
                            toggleColumn(action.key, event.target.checked)
                          }
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {sections.map((section) => (
                  <React.Fragment key={section.name}>
                    <TableRow>
                      <TableCell colSpan={ACTIONS.length + 1} className={classes.section}>
                        {section.name}
                      </TableCell>
                    </TableRow>
                    {section.menus.map((menu) => (
                      <TableRow key={menu.key} hover>
                        <TableCell>{menu.label}</TableCell>
                        {ACTIONS.map((action) => (
                          <TableCell key={action.key} className={classes.actionCell}>
                            {menu.actions.includes(action.key) ? (
                              <Checkbox
                                color="primary"
                                disabled={gridDisabled}
                                checked={grid[menu.key]?.[action.key] === true}
                                onChange={() => toggle(menu.key, action.key)}
                              />
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading || saving || targetIsSuperAdmin}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AdminPermissionsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  userId: PropTypes.string,
  userName: PropTypes.string,
};

export default AdminPermissionsDialog;
